/**
 * Composants UI partagés des panneaux RH retravaillés (LOT A).
 * Reprend le style des mockups validés (bande de stats, avatars initiales,
 * badges de statut, pastilles service). Réutilisés par tous les onglets RH.
 */
import type { ReactNode } from 'react';

const SERVICE_COLORS: Record<string, string> = {
  service_technique: 'var(--c-tech, #d85a30)',
  scolaire_cantine: 'var(--c-scolaire, #d4426a)',
  grc: 'var(--c-grc, #2f9e6f)',
  petite_enfance: 'var(--c-pe, #b06cd0)',
  urbanisme: 'var(--c-urba, #7f77dd)',
  comptable: 'var(--c-compta, #5aa0c8)',
};

const SERVICE_SHORT: Record<string, string> = {
  service_technique: 'Technique',
  scolaire_cantine: 'Scolaire',
  grc: 'Communication',
  petite_enfance: 'Petite enfance',
  urbanisme: 'Urbanisme',
  comptable: 'Comptabilité',
  associations: 'Associations',
  rh: 'RH',
};

/** Bande de statistiques en tête d'onglet. */
export function RhStats({ items }: { items: Array<{ label: string; value: ReactNode; sub?: string; tone?: 'default' | 'warn' | 'ok' | 'rh' }> }) {
  const toneClass = (t?: string) =>
    t === 'warn' ? 'text-warn' : t === 'ok' ? 'text-success' : t === 'rh' ? 'text-rh-fg' : 'text-ink';
  return (
    <div className="mb-4 grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))' }}>
      {items.map((s, i) => (
        <div key={i} className="rounded-r-lg border border-border bg-surface px-3.5 py-3">
          <div className="text-[11px] text-ink-subtle">{s.label}</div>
          <div className={`mt-0.5 text-2xl font-bold ${toneClass(s.tone)}`}>{s.value}</div>
          {s.sub && <div className="text-[11px] text-ink-subtle">{s.sub}</div>}
        </div>
      ))}
    </div>
  );
}

/** Avatar rond avec initiales (2 lettres). */
export function RhAvatar({ nom }: { nom: string | undefined }) {
  nom = nom || "?";
  const initiales = nom.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toLowerCase() || '?';
  return (
    <span className="inline-grid h-[30px] w-[30px] shrink-0 place-items-center rounded-full border border-border-hi bg-surface/60 text-[12px] font-semibold text-ink-muted">
      {initiales}
    </span>
  );
}

/** Nom d'agent avec avatar. */
export function RhWho({ nom }: { nom: string | undefined }) {
  nom = nom || "?";
  return (
    <span className="flex items-center gap-2.5">
      <RhAvatar nom={nom} />
      <span className="text-sm text-ink">{nom}</span>
    </span>
  );
}

/** Pastille de service colorée. */
export function RhServicePill({ service }: { service: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-r-pill border border-border-hi bg-surface/60 px-2 py-0.5 text-[11px] text-ink-muted">
      <span className="inline-block h-[7px] w-[7px] rounded-full" style={{ background: SERVICE_COLORS[service] ?? 'var(--ink-subtle)' }} />
      {SERVICE_SHORT[service] ?? service}
    </span>
  );
}

export { SERVICE_SHORT, SERVICE_COLORS };
